import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginUser, getLoggedInUser, verifyTOTPAndCompleteLogin, verifyBackupCodeAndCompleteLogin } from '../logic/loginLogic';
import './LoginPage.css';
import { playSound } from '../utils/soundPlayer';
import { useTranslation } from 'react-i18next'; // ✅
import JoinModal from '../components/JoinModal'; // adjust path if needed
import ForgotPasswordModal from "../components/ForgotPasswordModal"; // ⬅️ Import
import TOTPVerification from '../components/TOTPVerification';
import { useLoginRateLimit } from '../hooks/useRateLimit';
import RateLimitWarning from '../components/RateLimitWarning';
import ValidatedInput from '../components/ValidatedInput';



const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation(); // ✅
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [showTOTPVerification, setShowTOTPVerification] = useState(false);
  const [totpUser, setTotpUser] = useState(null);

  // Rate limiting for login attempts
  const rateLimit = useLoginRateLimit(email);

  const isLoggingIn = rateLimit.isLoading;


  useEffect(() => {
    const user = getLoggedInUser();
    if (user) {
      navigate('/menu');
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    // Check if rate limited
    if (rateLimit.isLimited) {
      setError(`Too many login attempts. Please try again in ${rateLimit.getFormattedTimeRemaining()}.`);
      return;
    }

    try {
      const result = await rateLimit.attemptAction(loginUser, email, password);
      if (result.success) {
        playSound('login');
        navigate('/menu');
      } else if (result.requiresTOTP) {
        // Show TOTP verification modal
        setTotpUser(result.user);
        setShowTOTPVerification(true);
      } else {
        setError(result.message || t('login.errorFallback'));
      }
    } catch (err) {
      setError(err?.message || t('login.errorFallback'));
    }
  };

  const handleTOTPVerificationSuccess = async (token) => {
    console.log('TOTP verification success, token:', token, 'user:', totpUser);
    const result = await verifyTOTPAndCompleteLogin(totpUser, token);
    console.log('TOTP verification result:', result);
    if (result.success) {
      playSound('login');
      setShowTOTPVerification(false);
      navigate('/menu');
    } else {
      setError(result.message);
    }
  };

  const handleBackupCodeSuccess = async (backupCode) => {
    const result = await verifyBackupCodeAndCompleteLogin(totpUser, backupCode);
    if (result.success) {
      playSound('login');
      setShowTOTPVerification(false);
      navigate('/menu');
    } else {
      setError(result.message);
    }
  };

  const handleTOTPCancel = () => {
    setShowTOTPVerification(false);
    setTotpUser(null);
    setError('');
  };

  showJoinModal && <JoinModal onClose={() => setShowJoinModal(false)} />

  return (

    <div className="login-container">
      <h1>{t('login.title')}</h1>
      <form onSubmit={handleLogin}>
        <div>
          <h3>{t('login.email')}</h3>
          <ValidatedInput
            type="email"
            ruleType="EMAIL"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('login.emailPlaceholder')}
            required
            showValidation={true}
          />
        </div>

        <div className="password-field-wrapper">
          <h3>{t('login.password')}</h3>
          <label className="label">
            <input
              type="password"
              className="emailText"
              placeholder={t('login.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <div className="forgot-link-text" onClick={() => setShowForgotModal(true)}>
            {t('login.forgot')}
          </div>
        </div>

        <button
          type="submit"
          className="login-submit-button"
          disabled={rateLimit.isLimited || isLoggingIn}
          aria-busy={isLoggingIn}
        >
          {isLoggingIn ? (
            <span className="login-loading-label">
              {t('login.button')}
              <span className="login-ellipsis" aria-hidden="true">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
          ) : (
            t('login.button')
          )}
        </button>
        
        <div className="login-inline-links">
          <span
            onClick={isLoggingIn ? undefined : () => setShowJoinModal(true)}
            className={`login-link-text ${isLoggingIn ? 'login-link-disabled' : ''}`}
            role="button"
            aria-disabled={isLoggingIn}
            tabIndex={isLoggingIn ? -1 : 0}
          >
            {t('login.join')}
          </span>
        </div>

        {showJoinModal && <JoinModal onClose={() => setShowJoinModal(false)} />}

      </form>

      {/* Rate limiting warning */}
      <RateLimitWarning
        isLimited={rateLimit.isLimited}
        remainingTime={rateLimit.remainingTime}
        attemptCount={rateLimit.attemptCount}
        maxAttempts={rateLimit.maxAttempts}
        isApproachingLimit={rateLimit.isApproachingLimit()}
        getFormattedTimeRemaining={rateLimit.getFormattedTimeRemaining}
        getProgressPercentage={rateLimit.getProgressPercentage}
        type="login"
      />

      {error && <p className="error-message">{error}</p>}
      {showForgotModal && <ForgotPasswordModal onClose={() => setShowForgotModal(false)} />}
      {showTOTPVerification && totpUser && (
        <TOTPVerification
          user={totpUser}
          onVerificationSuccess={handleTOTPVerificationSuccess}
          onCancel={handleTOTPCancel}
          onUseBackupCode={handleBackupCodeSuccess}
        />
      )}

    </div>
  );
};

export default LoginPage;
